import ClassNames from "classnames";
import { EmblaCarouselType } from "embla-carousel";
import { FunctionComponent, useState } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-8.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import { useDidMountEffect } from "../utils/hooks.js";
import {
  getGalleryMediaAspectRatio,
  getPlaceholderImage,
} from "../utils/utils.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ImageModule from "./Modules/ImageModule.js";
import MultilineText from "./MultilineText.js";
import Slider from "./Slider.js";
import SliderArrow from "./SliderArrow.js";

interface Props {
  title: string;
  subtitle?: string;
  textAlign: TitleAndSubtitleTextAlign;
  scheme: ColorScheme;
  isFirstOnPage: boolean;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  queries: ContainerQueries;
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  activeModuleId: string | undefined;
  onClickImage: (index: number) => void;
}

const ImageGalleryLayout8: FunctionComponent<Props> = ({
  imageModules,
  queries,
  languageId,
  pageId,
  moduleId,
  ...props
}) => {
  const site = useAppSelector(selectActiveSite);

  const images = imageModules.length
    ? imageModules
    : new Array(6)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  const showSlider = !queries["Query--large"];
  if (showSlider) {
    return (
      <ImageGalleryLayout8SliderVariant
        images={images}
        languageId={languageId}
        pageId={pageId}
        {...props}
      />
    );
  }

  return (
    <ImageGalleryLayout8CollageVariant
      images={images}
      pageId={pageId}
      {...props}
    />
  );
};

interface ImageGalleryLayout8VariantProps extends Omit<
  Props,
  "queries" | "imageModules" | "moduleId"
> {
  images: TranslatedModule<ImageModuleSettings>[];
}

const collageImageClasses = [
  styles["ImageGalleryLayout8__Image--left"],
  styles["ImageGalleryLayout8__Image--left-main"],
  styles["ImageGalleryLayout8__Image--left-center"],
  styles["ImageGalleryLayout8__Image--right-center"],
  styles["ImageGalleryLayout8__Image--right-main"],
  styles["ImageGalleryLayout8__Image--right"],
];

const ImageGalleryLayout8CollageVariant: FunctionComponent<
  Omit<ImageGalleryLayout8VariantProps, "languageId" | "activeModuleId">
> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  images,
  scheme,
  slides,
  isPreview,
  pageId,
  onClickImage,
}) => {
  const TagName = isFirstOnPage ? "h1" : "h2";
  const SubtitleTagName = title ? "div" : TagName;

  return (
    <div className={styles.ImageGalleryLayout8}>
      <div className={styles.ImageGalleryLayout8_Container}>
        {title && (
          <TagName
            className={ClassNames(
              moduleBaseStyles.Module__Title,
              styles.ImageGalleryLayout8__Title,
              `TextAlign--${textAlign.title}`,
              css({ color: scheme.main.title }),
            )}
          >
            {title}
          </TagName>
        )}
        {subtitle && (
          <SubtitleTagName
            className={ClassNames(
              moduleBaseStyles.Module__Subtitle,
              styles.ImageGalleryLayout8__Subtitle,
              `TextAlign--${textAlign.subtitle}`,
              css({ color: scheme.main.text }),
            )}
          >
            <MultilineText text={subtitle} />
          </SubtitleTagName>
        )}
        {images.map((imageModule, index) => {
          const item = slides[index];
          return (
            <a
              href={isPreview || !item ? undefined : item.src}
              className={ClassNames(
                styles.ImageGalleryLayout8__Image,
                collageImageClasses[index],
              )}
              key={imageModule.id}
              onClick={(e) => {
                e.preventDefault();
                onClickImage(index);
              }}
            >
              <ImageModule
                aspectRatio={getAspectRatio(index)}
                sizes="50vw"
                lazyLoad={true}
                width={1920}
                translatedModule={imageModule}
                isPreview={isPreview}
                isSlider={false}
                pageId={pageId}
                scheme={scheme}
                showOverlay={false}
              />
            </a>
          );
        })}
      </div>
    </div>
  );
};

const ImageGalleryLayout8SliderVariant: FunctionComponent<
  ImageGalleryLayout8VariantProps
> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  images,
  scheme,
  slides,
  isPreview,
  languageId,
  pageId,
  activeModuleId,
  onClickImage,
}) => {
  const [slider, setSlider] = useState<EmblaCarouselType>();
  const [slideIndex, setSlideIndex] = useState(0);

  const slideCount = Math.ceil(images.length / 3);

  useDidMountEffect(() => {
    if (!slider) return;

    const handleSlideChange = (slideApi: EmblaCarouselType) => {
      setSlideIndex(slideApi.selectedScrollSnap());
    };
    slider?.on("select", handleSlideChange);
    return () => {
      slider?.off("select", handleSlideChange);
    };
  }, [slider]);

  const arrowClassName = css({ fill: scheme.main.text });

  return (
    <div
      className={ClassNames(
        moduleBaseStyles.Module__Wrapper,
        moduleBaseStyles["Module__Wrapper--small-padding"],
        styles.ImageGalleryLayout8,
      )}
    >
      <ModuleHeadings
        textAlign={textAlign}
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        title={title}
        subtitle={subtitle}
      />
      <div>
        <Slider
          isPreview={isPreview}
          sliderEffect="slide"
          onSliderLoaded={setSlider}
          slidesToShow={1}
          activePreviewSlideId={activeModuleId}
          slideIds={images.map(({ id }) => id)}
          loop={false}
        >
          <div
            className={ClassNames(
              styles.ImageGalleryLayout8_Container,
              styles["ImageGalleryLayout8__Slide--slide-1"],
            )}
          >
            {images.slice(0, 3).map((imageModule, index) => {
              const item = slides[index];
              return (
                <a
                  href={isPreview || !item ? undefined : item.src}
                  className={styles.ImageGalleryLayout8__Item}
                  key={imageModule.id}
                  onClick={(e) => {
                    e.preventDefault();
                    onClickImage(index);
                  }}
                >
                  <ImageModule
                    aspectRatio={getAspectRatio(index)}
                    sizes="50vw"
                    lazyLoad={true}
                    width={1920}
                    translatedModule={imageModule}
                    isPreview={isPreview}
                    isSlider={false}
                    pageId={pageId}
                    scheme={scheme}
                    showOverlay={false}
                  />
                </a>
              );
            })}
          </div>
          {slideCount > 1 && (
            <div
              className={ClassNames(
                styles.ImageGalleryLayout8_Container,
                styles["ImageGalleryLayout8__Slide--slide-2"],
              )}
            >
              {images.slice(3, 6).map((imageModule, i) => {
                const index = i + 3;
                const item = slides[index];
                return (
                  <a
                    href={isPreview || !item ? undefined : item.src}
                    className={styles.ImageGalleryLayout8__Item}
                    key={imageModule.id}
                    onClick={(e) => {
                      e.preventDefault();
                      onClickImage(index);
                    }}
                  >
                    <ImageModule
                      aspectRatio={getAspectRatio(index)}
                      sizes="50vw"
                      lazyLoad={true}
                      width={1920}
                      translatedModule={imageModule}
                      isPreview={isPreview}
                      isSlider={false}
                      pageId={pageId}
                      scheme={scheme}
                      showOverlay={false}
                    />
                  </a>
                );
              })}
            </div>
          )}
        </Slider>
        {slideCount > 1 && (
          <div>
            <SliderArrow
              className={ClassNames(styles.ImageGalleryLayout8__SliderArrow, {
                [styles["ImageGalleryLayout8__SliderArrow--disabled"]]:
                  slideIndex === 0,
              })}
              iconClassName={ClassNames(
                styles.ImageGalleryLayout8__SliderArrowIcon,
                arrowClassName,
              )}
              glyph="arrow-back"
              direction="left"
              onClick={() => slider?.scrollPrev()}
              scheme={scheme}
              transparentBackground
              languageId={languageId}
            />
            <SliderArrow
              className={ClassNames(styles.ImageGalleryLayout8__SliderArrow, {
                [styles["ImageGalleryLayout8__SliderArrow--disabled"]]:
                  slideIndex === slideCount - 1,
              })}
              iconClassName={ClassNames(
                styles.ImageGalleryLayout8__SliderArrowIcon,
                arrowClassName,
              )}
              glyph="arrow-forward"
              direction="right"
              onClick={() => slider?.scrollNext()}
              scheme={scheme}
              transparentBackground
              languageId={languageId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const getAspectRatio = (index: number): number => {
  return getGalleryMediaAspectRatio(index, "layout-8");
};

export default ImageGalleryLayout8;
